import { StorageService } from '@amzn/awscat-portfolio-assessment-service-client';
import { SharedStorage } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useEffect, useState } from 'react';
import { match,useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { FormAction, modalActionButtonTextMap } from '../../common/form-builder/types';
import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import AWSUIFormBuilder, { MetadataItem } from '../common/AWSUIFormBuilder';
import { defaultStorageMetadata, sharedStorageColumnDefinition } from './SharedStorageItems';

type SharedStorageProps = FlashContextInterface;

const storageService = new StorageService();

const SharedStoragePage: FunctionComponent<SharedStorageProps> = ({flash}): JSX.Element => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;

    const defaultSharedStorage: SharedStorage = {
        accessedInfrequentlyPct: 0,
        excludeFromTco: [],
        maxIops: 200,
        objectService: '',
        sizeInGB: 1,
        storageCapacity: 'Raw',
        storageDesc: '',
        storageSystem: 'EBS',
        storageType: 'SAN',
    };

    const [ sharedStorageItems, setSharedStorageItems ] = useState<SharedStorage[]>([]);
    const [ selectedStorage, setSelectedStorage ] = useState<SharedStorage | null>(null);
    const [ modalVisible, setModalVisible ] = useState(false);
    const [ modalHeader, setModalHeader ] = useState('Add Shared Storage');
    const [ metadata, setMetadata ] = useState<MetadataItem<SharedStorage>[]>(defaultStorageMetadata);
    const [ modalStorageItem, setModalStorageItem ] = useState<SharedStorage>(defaultSharedStorage);
    const [ formAction, setFormAction ] = useState(FormAction.ADD);
    const [ loading, setLoading ] = useState(false);

    const isMounted = useIsMounted();

    async function getSharedStorage() {
        setLoading(true);
        try {
            if (portfolioId) {
                const sharedStorage: SharedStorage[] = await storageService.get(portfolioId);
                if (isMounted()) {
                    setSharedStorageItems(sharedStorage);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.STORAGE_ERROR_LOADING);
                setSharedStorageItems([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }
    useEffect(() => {
        getSharedStorage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const columnDefinition: ColumnDefinition<SharedStorage>[] = sharedStorageColumnDefinition;

    const updateSelectedStorage = (storages: readonly SharedStorage[] | undefined) => {
        const storage = storages?.length ? storages[0] : null;
        setSelectedStorage(storage);
    };

    const onAddStorageButtonClick = (): void => {
        setModalHeader(constants.STORAGE_ADD_MODAL_HEADER);
        setModalStorageItem(defaultSharedStorage);
        setFormAction(FormAction.ADD);
        setModalVisible(true);
    };
    
    const onEditStorageButtonClick = (): void => {
        setModalHeader(constants.STORAGE_EDIT_MODAL_HEADER);
        if (selectedStorage) {
            setModalStorageItem(selectedStorage);
        }
        setFormAction(FormAction.EDIT);
        setModalVisible(true);
    };

    const onDeleteStorageButtonClick = (): void => {
        setModalHeader(constants.STORAGE_DELETE_MODAL_HEADER);
        if (selectedStorage) {
            setModalStorageItem(selectedStorage);
        }
        setFormAction(FormAction.DELETE);
        setModalVisible(true);
    };

    const onFormSubmit = async (item: SharedStorage) => {
        setLoading(true);
        if (portfolioId) {
            const updatedStorage = sharedStorageItems;
            if (formAction === FormAction.ADD) {
                updatedStorage.push(item);
            }
            else {
                const index = updatedStorage.findIndex(storage => storage === selectedStorage);
                if (formAction === FormAction.DELETE) {
                    updatedStorage.splice(index, 1);
                }
                else {
                    updatedStorage[index] = {...item};
                }
            }
            await storageService.create(portfolioId, updatedStorage);
        }
        await getSharedStorage();
        setModalVisible(false);
    };

    // The following few functions deal with field validation, enabling, disabling, and hiding
    // form elements based on selections made by the user
    const getMetadataPropertyIndex = (propertyName: string): number => {
        return metadata.findIndex(property => property.identifier === propertyName);
    };

    const onAddEditUpdate = (updatedStorage: SharedStorage): SharedStorage => {
        if (formAction !== FormAction.DELETE && metadata.length) {
            const metadataToUpdate = [...metadata];
            const blockIndex = getMetadataPropertyIndex('storageSystem');
            const objectIndex = getMetadataPropertyIndex('objectService');

            // For SAN and NAS we want to hide the objectService UI
            // For SAN we want to set the storage System to EBS and disable changing it
            // For Object we want to hide the block storage UI itemsand set storage capacity to Usable
            // For Storage System set to EBS we want to enable maxIOPS input
            // For Object type, we want to hide Disk Type and EBS Volume Type
            // For NAS and storage system is not EBS, hide EBS Volume Type

            toggleStorageOptions(metadataToUpdate, updatedStorage, objectIndex, blockIndex,
                (updatedStorage.storageType === 'Object'),
                (updatedStorage.storageType === 'SAN'),
                (updatedStorage.storageType === 'Object'));

            toggleFrequencyPct(metadataToUpdate, updatedStorage);
            toggleMaxIOPS(metadataToUpdate, updatedStorage);
            toggleDiskType(metadataToUpdate, updatedStorage);
            toggleEbsVolumeType(metadataToUpdate, updatedStorage);
            setMetadata(metadataToUpdate);
        }
        return updatedStorage;
    };

    const toggleStorageOptions = (
        metadataToUpdate: MetadataItem<SharedStorage>[],
        updatedStorage: SharedStorage,
        objectIndex: number,
        blockIndex: number,
        blockUIHidden: boolean,
        blockUIDisabled = false,
        storageCapacityUIDisabled = false,
    ): void => {
        metadataToUpdate[objectIndex].uiHidden = !blockUIHidden;
        metadataToUpdate[blockIndex].uiHidden = blockUIHidden;
        metadataToUpdate[blockIndex].uiDisabled = blockUIDisabled;
        const index = getMetadataPropertyIndex('storageCapacity');
        metadataToUpdate[index].uiDisabled = storageCapacityUIDisabled;

        if (!blockUIHidden) { // if EBS/EFS options are shown
            // set EBS as default if none selected
            updatedStorage.storageSystem = updatedStorage.storageSystem || 'EBS';
            // cleanup of the objectService as it pertains to Object
            updatedStorage.objectService = '';
            if (updatedStorage.storageSystem === 'EFS') {
                // cleanup of the EBS specific attributes
                updatedStorage.ebsVolumeType = '';
                updatedStorage.maxIops = 0;
            }
        } else { // if Object options are shown 
            // Usable is the default storageCapacity for Object
            updatedStorage.storageCapacity = 'Usable';
            // set S3Standard as default if none selected
            updatedStorage.objectService = updatedStorage.objectService || 'S3Standard';
            // cleanup of the storageSystem
            updatedStorage.storageSystem = '';
            // cleanup of the percentage when Glacier
            if (updatedStorage.objectService === 'S3Glacier') {
                updatedStorage.accessedInfrequentlyPct = 0;
            }
        }
    };

    const toggleFrequencyPct = (metadataToUpdate: MetadataItem<SharedStorage>[], updatedStorage: SharedStorage): void => {
        const index = getMetadataPropertyIndex('accessedInfrequentlyPct');
        metadataToUpdate[index].uiHidden = !(
            updatedStorage.storageType === 'Object' &&
            updatedStorage.objectService === 'S3Standard'
        );
        if (updatedStorage.storageType !== 'Object') {
            updatedStorage.accessedInfrequentlyPct = 0;
        }
    };

    const toggleMaxIOPS = (metadataToUpdate: MetadataItem<SharedStorage>[], updatedStorage: SharedStorage): void => {
        const index = getMetadataPropertyIndex('maxIops');
        metadataToUpdate[index].uiHidden = !(
            updatedStorage.storageType !== 'Object' &&
            updatedStorage.storageSystem === 'EBS'
        );
        if (updatedStorage.storageType === 'Object') {
            updatedStorage.maxIops = 0;
        }
    };

    const toggleDiskType = (metadataToUpdate: MetadataItem<SharedStorage>[], updatedStorage: SharedStorage): void => {
        const index = getMetadataPropertyIndex('diskType');
        metadataToUpdate[index].uiHidden = updatedStorage.storageType === 'Object';
    };

    const toggleEbsVolumeType = (metadataToUpdate: MetadataItem<SharedStorage>[], updatedStorage: SharedStorage): void => {
        const index = getMetadataPropertyIndex('ebsVolumeType');
        metadataToUpdate[index].uiHidden = updatedStorage.storageType === 'Object' ||
            (updatedStorage.storageType === 'NAS' && updatedStorage.storageSystem !== 'EBS');
        if (updatedStorage.storageType === 'Object') {
            updatedStorage.ebsVolumeType = '';
        }
    };

    const modalSize = formAction === FormAction.DELETE ? 'medium' : 'small';
    const deleteErrorText = (
        <span>
            <h3 className='awscatui-mt-n'>{constants.FORM_DELETE_ERROR_TITLE}</h3>
            <p>{`${constants.FORM_DELETE_ERROR_CONTENT} ${constants.STORAGE}. ${constants.FORM_DELETE_ERROR_PROCEED}`}</p>
        </span>
    );

    return (
        <span>
            <AWSCATTable
                resourceName={constants.STORAGE_TABLE_HEADER}
                items={sharedStorageItems}
                columnDefinitions={columnDefinition}
                loading={loading}
                selectionType='single'
                autoGenerateVisibleContent={true}
                headerActions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button disabled={loading || !selectedStorage} onClick={onEditStorageButtonClick}>{constants.EDIT_BUTTON_TEXT}</Button>
                        <Button disabled={loading || !selectedStorage} onClick={onDeleteStorageButtonClick}>{constants.DELETE_BUTTON_TEXT}</Button>
                        <Button disabled={loading} onClick={onAddStorageButtonClick} variant="primary">{constants.STORAGE_ADD_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                }
                onSelectionChange={updateSelectedStorage}
                useDefaultDownload
            />
            <Modal size={modalSize} header={modalHeader} visible={modalVisible} onDismiss={() => setModalVisible(false)}>
                <AWSUIFormBuilder
                    item={modalStorageItem}
                    metadata={metadata}
                    loading={loading}
                    readonly={formAction === FormAction.DELETE}
                    formDeleteMessage={formAction === FormAction.DELETE ? deleteErrorText : undefined}
                    primaryAction={modalActionButtonTextMap.get(formAction)}
                    onCancel={() => setModalVisible(false)}
                    onUpdateWithStateChange={onAddEditUpdate}
                    onSubmit={onFormSubmit}
                />
            </Modal>
        </span>
    );
};

export default withFlashContext(SharedStoragePage);
